import { useEffect, useState } from 'react';

export type SwjBarPhase = 'expanded' | 'collapsed' | 'entering' | 'leaving';

// Duration of the SWJ top-bar show/hide slide (kept in sync with the CSS transition).
const SWJ_BAR_ANIMATION_MS = 300;

// Target phase when the bar's expansion changes. `animate` is only true for a genuine
// user toggle within the same orientation; an orientation change snaps straight to the
// idle state so the top zone doesn't play a show/hide animation.
const resolveTargetPhase = (
  isExpanded: boolean,
  animate: boolean,
): SwjBarPhase => {
  if (isExpanded) {
    return animate ? 'entering' : 'expanded';
  }
  return animate ? 'leaving' : 'collapsed';
};

const buildSlotClass = (phase: SwjBarPhase): string => {
  if (phase === 'collapsed') return 'slot jackpot swj-standby';
  if (phase === 'entering') return 'slot jackpot top-bar swj-entering';
  if (phase === 'leaving') return 'slot jackpot top-bar swj-leaving';
  return 'slot jackpot top-bar';
};

/**
 * Drives the SWJ top-bar show/hide slide and returns the slot className for the bar.
 *
 * Both directions animate (and nothing animates on initial load or on an orientation
 * change): `entering`/`leaving` add the animation for one 0.3s cycle, then the phase
 * settles to `expanded`/`collapsed`.
 */
export const useSwjBarPhase = (
  isBarExpanded: boolean,
  isPortrait: boolean,
): string => {
  const [barPhase, setBarPhase] = useState<SwjBarPhase>(
    isBarExpanded ? 'expanded' : 'collapsed',
  );
  const [prevExpanded, setPrevExpanded] = useState(isBarExpanded);
  const [prevPortrait, setPrevPortrait] = useState(isPortrait);

  // Kick off the phase during render when the expansion changes (the recommended
  // alternative to calling setState synchronously inside an effect).
  if (prevExpanded !== isBarExpanded || prevPortrait !== isPortrait) {
    const orientationChanged = prevPortrait !== isPortrait;
    const expansionChanged = prevExpanded !== isBarExpanded;
    setPrevExpanded(isBarExpanded);
    setPrevPortrait(isPortrait);

    if (expansionChanged) {
      setBarPhase(resolveTargetPhase(isBarExpanded, !orientationChanged));
    }
  }

  // Once the 0.3s animation has played, settle into the idle state.
  useEffect(() => {
    if (barPhase !== 'entering' && barPhase !== 'leaving') return;
    const settledPhase = barPhase === 'entering' ? 'expanded' : 'collapsed';
    const timeout = setTimeout(
      () => setBarPhase(settledPhase),
      SWJ_BAR_ANIMATION_MS,
    );
    return () => clearTimeout(timeout);
  }, [barPhase]);

  return buildSlotClass(barPhase);
};
